import { ReactElement, useCallback, useState } from 'react';

import { GetServerSidePropsContext, InferGetServerSidePropsType } from 'next';
import { getCsrfToken } from 'next-auth/react';
import { getServerSession } from 'next-auth/next';

import { DEFAULT_PATH } from 'config';
import Page from 'components/Page';
import Layout from 'layout';
import { authOptions } from 'pages/api/auth/[...nextauth]';
import AuthLoginTemplate from 'sections/auth/AuthLoginTemplate';
import AuthLogin from 'sections/auth/auth-forms/AuthLogin';
import UnverifiedEmail from 'sections/auth/auth-forms/UnVerifiedEmail';

export default function SignIn({ csrfToken }: InferGetServerSidePropsType<typeof getServerSideProps>) {
  const [showUnverifiedEmail, setShowUnverifiedEmail] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const handleUnverified = useCallback((userEmail: string, userPassword: string) => {
    setEmail(userEmail);
    setPassword(userPassword);
    setShowUnverifiedEmail(true);
  }, []);

  return (
    <Page title="Login" titleKey="page.login">
      {showUnverifiedEmail ? (
        <UnverifiedEmail email={email} password={password} />
      ) : (
        <AuthLoginTemplate>
          <AuthLogin csrfToken={csrfToken} onUnverified={handleUnverified} />
        </AuthLoginTemplate>
      )}
    </Page>
  );
}

SignIn.getLayout = function getLayout(page: ReactElement) {
  return <Layout variant="auth">{page}</Layout>;
};

export async function getServerSideProps(context: GetServerSidePropsContext) {
  const session = await getServerSession(context.req, context.res, authOptions);
  if (session?.user) {
    return {
      redirect: {
        destination: DEFAULT_PATH,
        permanent: false
      }
    };
  }

  let csrfToken: string | null = null;

  try {
    csrfToken = (await getCsrfToken(context)) ?? null;
  } catch {
    const host = context.req?.headers?.host;
    const protocol = host?.includes('localhost') ? 'http' : 'https';
    const baseUrl = host ? `${protocol}://${host}` : '';

    if (baseUrl) {
      try {
        const csrfRes = await fetch(`${baseUrl}/api/auth/csrf`);
        if (csrfRes.ok) {
          const csrfJson = await csrfRes.json();
          csrfToken = csrfJson?.csrfToken ?? null;
        }
      } catch {
        csrfToken = null;
      }
    }
  }

  return {
    props: { csrfToken: csrfToken ?? null }
  };
}
